import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard } from 'guards/globalGuards'
import MonthlyBarChart, { MonthlyBarChartProps } from 'components/features/pfm/MonthlyBarChart/MonthlyBarChart'
import { getMonthlyTrendChanges } from 'helpers/trendHelpers'
import { peergroupGenericTrendsGuard } from 'guards/genericGuards'
import { memberNetworthChangeGuard } from 'guards/networthGuards'
import { yourPeersGenericTrendsSelector, othersGenericTrendsSelector, memberNetworthChangeSelector } from 'selectors/genericSelectors'
import { GenericTrendOwnProps } from 'interfaces/trend'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { selectedRelationSelector } from 'selectors/peergroupSelectors'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

type OwnProps =
  & GenericTrendOwnProps

const createMapStateToProps = () =>
  createSelector(
    memberNetworthChangeSelector,
    yourPeersGenericTrendsSelector,
    othersGenericTrendsSelector,
    selectedRelationSelector,
    isMobileSelector,
    withComparisonSelector,
    (memberChange, peersTrend, otherTrend, selectedRelation, isMobile, withComparison): MonthlyBarChartProps => {
      if (!memberChange) {
        throw createForgottenGuardError('NetworthMonthlyChangeModuleContainer', 'memberNetworthChangeGuard')
      }

      if (!peersTrend || !otherTrend) {
        throw createForgottenGuardError('NetworthMonthlyChangeModuleContainer', 'peergroupGenericTrendsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('NetworthMonthlyChangeModuleContainer', 'peergroupsGuard')
      }

      return {
        memberActualValues: getMonthlyTrendChanges(memberChange.actual),
        peerActualValues: getMonthlyTrendChanges(peersTrend.actual),
        otherActualValues: getMonthlyTrendChanges(otherTrend.actual),
        isMobile,
        selectedPeergroupName: selectedRelation.name,
        withComparison,
      }
    }
  ) as (state: State, ownProps: OwnProps) => MonthlyBarChartProps

const guards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    memberNetworthChangeGuard,
    peergroupGenericTrendsGuard,
  ),
]

const container = connectShadedLoadable<
  MonthlyBarChartProps,
  {},
  {},
  OwnProps
>(createMapStateToProps, undefined, guards)(MonthlyBarChart)

export default container
